<template>
  <div class="sk-login-page">
    <div class="sk-topbar">
      <a href="#" class="sk-logo" @click.prevent>
        <img src="https://sentrykit.com/wp-content/uploads/2026/04/Sk-2.0-logo-white.png" alt="SentryKit" style="height:48px;width:auto;display:block;">
      </a>
    </div>

    <div class="sk-main">
      <div class="sk-card">
        <div class="sk-card-header">
          <h2>Log in to your account</h2>
          <p>Welcome back. Enter your credentials to continue.</p>
        </div>

        <div v-if="error" class="sk-error-msg">
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="2"/><path stroke-width="2" d="M12 8v4m0 4h.01"/></svg>
          {{ error }}
        </div>

        <button type="button" class="sk-google-btn" @click="continueWithGoogle">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <p class="sk-google-hint">Signed up with Google? Use Continue with Google, or <router-link to="/forgotpassword">reset your password</router-link>.</p>
        <div class="sk-or">or log in with email</div>

        <form @submit.prevent="login">
          <div class="sk-form-row">
            <div class="sk-field">
              <label>Email Address <span class="req">*</span></label>
              <input class="sk-input" type="email" v-model="email" placeholder="you@company.com" required autofocus>
            </div>
          </div>

          <div class="sk-form-row sk-mt-4">
            <div class="sk-field">
              <label>Password <span class="req">*</span></label>
              <div class="sk-password-wrap">
                <input class="sk-input" :type="showPassword ? 'text' : 'password'" v-model="password" placeholder="Enter your password" required>
                <button type="button" class="sk-toggle-pw" @click="showPassword = !showPassword">
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                </button>
              </div>
            </div>
          </div>

          <div class="sk-login-opts sk-mt-4">
            <router-link to="/forgotpassword" class="sk-forgot-link">Forgot password?</router-link>
          </div>

          <button type="submit" class="sk-btn sk-btn-primary sk-mt-4" :class="{ loading: working }" :disabled="working">
            <span v-if="working" class="sk-spinner"></span>
            <span v-else>Log In →</span>
          </button>
        </form>

        <div class="sk-login-link">Don't have an account? <router-link to="/signup">Sign up free</router-link></div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/signup-flow.css'
import { pushGtmEvent } from './utils/gtmEvents'
import { GOOGLE_SSO_ERROR_MESSAGES, startGoogleSignIn } from './utils/googleSignIn'

export default {
  data: function () {
    return {
      email: '',
      password: '',
      keepMeSignedIn: true,
      showPassword: false,
      error: '',
      working: false
    }
  },
  created: function () {
    var reason = this.$route.query.sso_error
    if (reason) {
      this.error = GOOGLE_SSO_ERROR_MESSAGES[reason] || GOOGLE_SSO_ERROR_MESSAGES.google_failed
      pushGtmEvent({ event: 'sso_error', provider: 'google', reason: String(reason) })
    }
  },
  methods: {
    continueWithGoogle: function () {
      startGoogleSignIn('login', '/login')
    },
    login: function () {
      this.working = true
      this.error = ''
      this.$http.post('/api/auth/login/standard', {
        email: this.email,
        password: this.password,
        keepMeSignedIn: this.keepMeSignedIn
      }).then(
        () => {
          this.working = false
          this.$root.pendingPostCredentialsRedirect = true
          this.$root.checkIfAuthenticated()
        },
        (response) => {
          this.working = false
          this.error = response.body.message || 'Login failed. Please check your credentials.'
          setTimeout(() => { this.error = '' }, 9000)
        }
      )
    }
  }
}
</script>

<style scoped>
.sk-login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Roboto', 'DM Sans', -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.sk-google-btn { width: 100%; height: 48px; display: flex; align-items: center; justify-content: center; gap: 10px; background: #fff; border: 1.5px solid rgba(18,46,82,0.22); border-radius: 11px; color: #0d1f35; font: 500 15px 'Roboto', sans-serif; cursor: pointer; transition: border-color .15s, background .15s; }
.sk-google-btn:hover { border-color: #bfcbd9; background: #FAFCFE; }
.sk-google-hint { margin: 8px 0 0; font-size: 12.5px; color: #4A6080; text-align: center; }
.sk-google-hint a { color: var(--sk-orange, #F5841F); text-decoration: none; font-weight: 500; }
.sk-or { display: flex; align-items: center; gap: 12px; color: #8099B8; font-size: 13px; margin: 18px 0; }
.sk-or::before, .sk-or::after { content: ""; flex: 1; height: 1px; background: rgba(18,46,82,0.12); }

.sk-login-opts {
  display: flex;
  justify-content: flex-end;
}

.sk-forgot-link {
  font-size: 13px;
  color: var(--sk-orange, #F5841F);
  text-decoration: none;
  font-weight: 500;
}

.sk-forgot-link:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// LoginPage.vue?0fdad772